import {
  ArrowDownIcon,
  ArrowUpIcon,
  IconButton,
  SlidersHIcon,
  Theme,
  ThumbtackIcon,
  Tooltip,
  makeStyles,
} from '@forgeglobal/design-framework'
import { IHeaderParams } from 'ag-grid-community'
import { PinnedMenu } from 'isolated-design-library/components/tables/PinnedMenu'
import React, { MouseEvent, useRef, useState } from 'react'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) => ({
  active: {
    background: '#E7ECEF',
    color: theme.palette.secondary.main,
    borderRight: '1px solid #f8fafb', //distinction between columns of the same color
    borderLeft: '1px solid #f8fafb',
    '&:hover': {
      background: '#e1e7ea',
    },
  },
  wrapper: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 3),
    overflow: 'hidden',
    '&:hover $actions': {
      position: 'relative',
      width: 'auto',
      right: 0,
      pointerEvents: 'initial',
      '& > *': {
        opacity: 1,
      },
    },
    '&$active:not(:hover)': {
      paddingRight: theme.spacing(6), // make space for the filter icon
    },
  },
  icon: {
    fontSize: 20,
    margin: 2,
    color: theme.palette.text.secondary,
    '$active &': {
      color: theme.palette.secondary.main,
    },
  },
  filterIcon: {
    '$active &': {
      color: theme.palette.secondary.main,
    },
  },
  label: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  actions: {
    position: 'absolute',
    right: theme.spacing(3),
    pointerEvents: 'none',
    transition: 'opacity 0.1s ease 0.1s',
    '& > *:not(.ag-grid-has-filter)': {
      transition: 'opacity 0.1s ease 0.1s',
      opacity: 0,
    },
    '$active &': {
      right: theme.spacing(3) + 1, // account for border-right when active
    },
  },
}))

export const TableHeader = (params: IHeaderParams) => {
  const [counter, setCounter] = useState(0)
  const [pinnedMenuOpen, setPinnedMenuOpen] = useState(false)
  const rerender = () => setCounter(counter + 1)
  const wrapperRef = useRef(null)
  const styles = useStyles()

  const toggleSort = (e: any) => {
    if (!params.enableSorting || pinnedMenuOpen) return
    if (params.column.isSortNone()) params.column.setSort('asc', e.shiftKey)
    else if (params.column.isSortAscending()) params.column.setSort('desc', e.shiftKey)
    else params.column.setSort('', e.shiftKey)
    params.api.onSortChanged() // Trigger callbacks
    rerender()
  }

  const showPinned = (event: MouseEvent) => {
    event.stopPropagation()
    setPinnedMenuOpen(true)
  }

  const showFilters = (event: MouseEvent) => {
    event.stopPropagation()
    params.showColumnMenu(wrapperRef.current!)
  }

  const hasFilter = params.columnApi.getColumn(params.column).isFilterActive()
  const hasFilterCapability = !!params.column.getColDef().filter
  return (
    <div onClick={toggleSort} className={clsx(styles.wrapper, { [styles.active]: hasFilter })} ref={wrapperRef}>
      <div className={styles.label}>{params.displayName}</div>
      {params.enableSorting && params.column.isSortAscending() && <ArrowUpIcon className={styles.icon} />}
      {params.enableSorting && params.column.isSortDescending() && <ArrowDownIcon className={styles.icon} />}
      <div style={{ flexGrow: 1 }} />
      <div className={styles.actions}>
        <Tooltip title="Pin column" placement="top" enterDelay={1000}>
          <IconButton size="small" onClick={showPinned}>
            <ThumbtackIcon />
          </IconButton>
        </Tooltip>
        {hasFilterCapability && (
          <Tooltip title="Filter column" placement="top" enterDelay={1000}>
            <IconButton
              size="small"
              onClick={showFilters}
              className={styles.filterIcon + ((hasFilter && ' ag-grid-has-filter') || '')}>
              <SlidersHIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      {pinnedMenuOpen && (
        <PinnedMenu
          open={pinnedMenuOpen}
          onClose={() => setPinnedMenuOpen(false)}
          anchorEl={wrapperRef.current || null}
          title={params.displayName}
          params={params}
        />
      )}
    </div>
  )
}
